import { useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Button from '@/components/ui/Button'
import TextField from '@/components/ui/TextField'
import { AuthTimeoutError, signIn, useAuth } from '@/auth/useAuth'
import AuthShell from './AuthShell'

export default function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { session, isLoading } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!isLoading && session) {
    const from = (location.state as { from?: string } | null)?.from
    return <Navigate to={from ?? '/'} replace />
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setIsSubmitting(true)
    try {
      await signIn(email, password)
      void navigate('/', { replace: true })
    } catch (caught) {
      // Bewusst keine Unterscheidung zwischen "Konto unbekannt" und "Passwort
      // falsch" — das verriete, welche Adressen registriert sind. Eine
      // Zeitüberschreitung ist dagegen kein Anmeldefehler und wird als solche
      // benannt, sonst sucht man den Fehler beim eigenen Passwort.
      setError(
        caught instanceof AuthTimeoutError
          ? t('auth.errors.timeout')
          : t('auth.errors.invalidCredentials'),
      )
      console.error(caught)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthShell title={t('auth.login.title')} subtitle={t('auth.login.subtitle')}>
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        <TextField
          label={t('auth.fields.email')}
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          label={t('auth.fields.password')}
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        {error && (
          <p role="alert" className="text-over text-sm">
            {error}
          </p>
        )}

        <Button type="submit" isLoading={isSubmitting} className="w-full">
          {isSubmitting ? t('auth.login.submitting') : t('auth.login.submit')}
        </Button>
      </form>

      <p className="text-roast-500 dark:text-crema-300/70 mt-6 text-center text-sm">
        {t('auth.login.noAccount')}{' '}
        <Link to="/register" className="text-roast-600 dark:text-crema-200 font-medium underline">
          {t('auth.login.toRegister')}
        </Link>
      </p>
    </AuthShell>
  )
}
